import { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Users, Calendar, Clock, Receipt, 
  DollarSign, FileText, Award, BarChart3, ClipboardList,
  Upload, Settings, ChevronDown, LogOut, User, Building2,
  ChevronLeft, PenTool, Bell, Volume2, VolumeX
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { API } from '@/pages/hr/api';
import hrApiClient from '@/pages/hr/api';
import { toast } from 'sonner';

// Import HR API to register auth interceptors
import '@/pages/hr/api';

const navigation = [
  { name: 'Dashboard', href: '/suite/hr', icon: LayoutDashboard, exact: true, adminOnly: true },
  { name: 'Onboarding', href: '/suite/hr/onboarding', icon: ClipboardList, adminOnly: true },
  { name: 'Documents', href: '/suite/hr/documents', icon: PenTool, adminOnly: true },
  { name: 'Offer Templates', href: '/suite/hr/offer-templates', icon: FileText, adminOnly: true },
  { name: 'Employees', href: '/suite/hr/employees', icon: Users, adminOnly: true },
  { name: 'Leave', href: '/suite/hr/leave', icon: Calendar, adminOnly: true },
  { name: 'Attendance', href: '/suite/hr/attendance', icon: Clock, adminOnly: true },
  { name: 'Claims', href: '/suite/hr/claims', icon: Receipt, adminOnly: true },
  { name: 'Payroll', href: '/suite/hr/payroll', icon: DollarSign, adminOnly: true },
  { name: 'Payslips', href: '/suite/hr/payslips', icon: FileText, adminOnly: true },
  { name: 'Performance', href: '/suite/hr/performance', icon: Award, adminOnly: true },
  { name: 'Reports', href: '/suite/hr/reports', icon: BarChart3, adminOnly: true },
  { name: 'Forms', href: '/suite/hr/forms', icon: ClipboardList, adminOnly: true },
  { name: 'Import Data', href: '/suite/hr/import', icon: Upload, adminOnly: true },
];

// Employee self-service navigation (for non-admin users)
const employeeNavigation = [
  { name: 'My Dashboard', href: '/suite/hr', icon: LayoutDashboard, exact: true },
  { name: 'My Leave', href: '/suite/hr/my-leave', icon: Calendar },
  { name: 'My Attendance', href: '/suite/hr/my-attendance', icon: Clock },
  { name: 'My Claims', href: '/suite/hr/my-claims', icon: Receipt },
  { name: 'My Payslips', href: '/suite/hr/my-payslips', icon: FileText },
  { name: 'My Profile', href: '/suite/hr/my-profile', icon: User },
];

const adminNavigation = [
  { name: 'Settings', href: '/suite/hr/settings', icon: Settings },
];

export default function HRLayout() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('hr_notification_sound');
    return saved !== 'false'; // Default to true
  });
  const prevUnreadRef = useRef(0);
  const audioRef = useRef(null);
  
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const hrUserStr = localStorage.getItem('hrUser');
  const user = hrUserStr ? JSON.parse(hrUserStr) : userData;

  // Determine if user is HR admin (can be through main role or hr_role)
  const isAdmin = user.role === 'super_admin' || user.role === 'platform_admin' || user.role === 'hr_admin' || 
                  user.hr_role === 'hr_director' || user.hr_role === 'hr_manager' || user.hr_role === 'admin';

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }
  }, [soundEnabled]);

  // Toggle sound
  const toggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem('hr_notification_sound', newValue.toString());
    toast.success(newValue ? 'Notification sound enabled' : 'Notification sound muted');
  };

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await hrApiClient.get('/notifications');
        const newNotifications = response.data.notifications || [];
        const newUnreadCount = response.data.unread_count || 0;
        
        // Play sound if there are new notifications
        if (newUnreadCount > prevUnreadRef.current && prevUnreadRef.current !== 0) {
          playNotificationSound();
          toast.info('New notification received!');
        }
        
        setNotifications(newNotifications);
        setUnreadCount(newUnreadCount);
        prevUnreadRef.current = newUnreadCount;
      } catch (error) {
        console.log('Could not fetch notifications');
      }
    };
    
    fetchNotifications();
    // Poll every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [playNotificationSound]);

  const markAsRead = async (notificationId) => {
    try {
      await hrApiClient.post(`/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(n => 
        n.notification_id === notificationId ? {...n, read: true} : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await hrApiClient.post('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({...n, read: true})));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('hrUser');
    localStorage.removeItem('hrToken');
    navigate('/portal');
  };

  return (
    <div className="flex h-screen bg-slate-950">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-slate-900 border-r border-slate-800
        transform transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        flex flex-col
      `}>
        {/* Header */}
        <div className="p-4 border-b border-slate-800">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/portal')}
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800 mb-3"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            All Products
          </Button>
          <div className="flex items-center gap-3 px-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-white">MyHR Orbit</h2>
              <p className="text-xs text-slate-400">HR Management System</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {/* Show employee navigation for non-admins, admin navigation for admins */}
          {(isAdmin ? navigation : employeeNavigation).map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              end={item.exact}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isActive
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              <span>{item.name}</span>
            </NavLink>
          ))}
          
          {/* Admin section */}
          {isAdmin && (
            <>
              <div className="pt-4 pb-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-3">Admin</p>
              </div>
              {adminNavigation.map((item) => (
                <NavLink
                  key={item.href}
                  to={item.href}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      isActive
                        ? 'bg-blue-600 text-white'
                        : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                    }`
                  }
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.name}</span>
                </NavLink>
              ))}
            </>
          )}
        </nav>

        {/* User info */}
        <div className="p-4 border-t border-slate-800">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="w-full justify-start text-left">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium text-sm">
                    {(user.name || user.full_name || 'U')[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {user.name || user.full_name || 'User'}
                    </p>
                    <p className="text-xs text-slate-400 truncate">
                      {user.role || 'HR User'}
                    </p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-slate-800 border-slate-700">
              <DropdownMenuItem 
                onClick={() => navigate('/suite/hr/settings')}
                className="text-slate-300 hover:bg-slate-700 cursor-pointer"
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-700" />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="text-red-400 hover:bg-slate-700 cursor-pointer"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Back to Portal
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 overflow-auto bg-slate-950">
        {/* Notification sound audio element */}
        <audio ref={audioRef} preload="auto">
          <source src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleUs/LLHj/9yUiJ2IlYCWlYNkWG+LlqWonJaQg3dvdJWkpaien5yPfnF1hZidn56dnY+Bf32Djo+RkJKQjId/fX+EiIqKi4uJh4R/fX2AhIaHh4eGhYOBf36AgoSDg4ODAoGAf39/gIGBgYGBgIB/f39/f4CAgICAf4B/f35+fn9/f39/f35+fn5+fn5+fn5+fn5+fn5+fn5+fn5+fn5+fn5+fn5+fn5+fn5+fn5+fg==" type="audio/wav" />
        </audio>
        
        {/* Top bar with notifications */}
        <div className="sticky top-0 z-20 bg-slate-950/95 backdrop-blur border-b border-slate-800 px-6 py-3 flex items-center justify-end gap-2">
          {/* Sound toggle button */}
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={toggleSound}
            className="text-slate-400 hover:text-white"
            title={soundEnabled ? 'Mute notifications' : 'Unmute notifications'}
          >
            {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
          </Button>
          
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="relative text-slate-400 hover:text-white">
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0 bg-slate-800 border-slate-700" align="end">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
                <h4 className="font-semibold text-slate-100">Notifications</h4>
                {unreadCount > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={markAllAsRead}
                    className="text-xs text-blue-400 hover:text-blue-300"
                  >
                    Mark all read
                  </Button>
                )}
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-slate-500">
                    No notifications
                  </div>
                ) : (
                  notifications.map(notification => (
                    <div
                      key={notification.notification_id}
                      className={`px-4 py-3 border-b border-slate-700 hover:bg-slate-700/50 cursor-pointer ${
                        !notification.read ? 'bg-slate-700/30' : ''
                      }`}
                      onClick={() => !notification.read && markAsRead(notification.notification_id)}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-2 h-2 rounded-full mt-2 ${!notification.read ? 'bg-blue-500' : 'bg-slate-600'}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-200">{notification.title}</p>
                          <p className="text-xs text-slate-400 mt-1">{notification.message}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            {new Date(notification.created_at).toLocaleDateString('en-MY', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>
        
        {/* Content area */}
        <div className="p-6">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
